import React, { memo } from 'react';
import { Box, Stack, Typography } from 'investira.react.components';

const DEFAULT_RANGES = [
  { from: 0, to: 1.8, color: 'error.main' },
  { from: 1.8, to: 2.2, color: 'warning.main' },
  { from: 2.2, to: 3, color: 'info.main' },
  { from: 3, to: 5, color: 'primary.main' }
];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const InlineGauge = memo(props => {
  const {
    value = 0,
    min = 0,
    max = 5,
    ranges = DEFAULT_RANGES,
    ticks = null,
    height = 12,
    borderRadius = 2,
    showValue = true,
    valueSuffix = '',
    label = null,
    trackColor = 'grey.100',
    trackerWidth = 12,
    trackerHeight = 8,
    trackerColor = 'grey.100'
  } = props;

  const safeMin = Number.isFinite(min) ? min : 0;
  const safeMax = Number.isFinite(max) && max > safeMin ? max : safeMin + 1;
  const safeValue = Number.isFinite(value) ? clamp(value, safeMin, safeMax) : safeMin;
  const total = safeMax - safeMin;

  const normalizePercent = point => ((point - safeMin) / total) * 100;

  const gaugeRanges = Array.isArray(ranges) && ranges.length > 0 ? ranges : DEFAULT_RANGES;
  const parsedRanges = gaugeRanges
    .map(range => {
      const from = clamp(Number(range?.from ?? safeMin), safeMin, safeMax);
      const to = clamp(Number(range?.to ?? safeMax), safeMin, safeMax);
      return {
        from: Math.min(from, to),
        to: Math.max(from, to),
        color: range?.color || 'primary.main'
      };
    })
    .sort((a, b) => a.from - b.from);

  const parsedTicks = Array.isArray(ticks) && ticks.length > 0 ? ticks : [safeMin, safeMax];
  const progress = normalizePercent(safeValue);
  const safeTrackerWidth = Math.max(Number(trackerWidth) || 0, 0);
  const safeTrackerHeight = Math.max(Number(trackerHeight) || 0, 0);

  return (
    <Stack gap={0.75}>
      {(label || showValue) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {label ? (
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
          ) : (
            <Box />
          )}
          {showValue && (
            <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 600 }}>
              {safeValue.toFixed(2)}
              {valueSuffix}
            </Typography>
          )}
        </Stack>
      )}

      <Box sx={{ position: 'relative', pt: `${safeTrackerHeight}px` }}>
        <Box
          sx={{
            position: 'relative',
            height,
            overflow: 'hidden',
            borderRadius,
            backgroundColor: trackColor
          }}>
          {parsedRanges.map((range, index) => {
            const left = normalizePercent(range.from);
            const width = normalizePercent(range.to) - left;
            return (
              <Box
                key={`${range.from}-${range.to}-${index}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: `${left}%`,
                  width: `${Math.max(width, 0)}%`,
                  height: '100%',
                  backgroundColor: range.color
                }}
              />
            );
          })}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: `calc(${progress}% - ${safeTrackerWidth / 2}px)`,
            width: `${safeTrackerWidth}px`,
            height: `${safeTrackerHeight}px`,
            backgroundColor: trackerColor,
            clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
            zIndex: 2
          }}
        />
      </Box>

      <Stack direction="row" justifyContent="space-between">
        {parsedTicks.map((tick, index) => (
          <Typography key={`${tick}-${index}`} variant="caption" color="textSecondary">
            {Number(tick).toFixed(1)}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
});

export default InlineGauge;
